import { equals, isEmpty, isNotNil, uniq } from 'rambda';
import {
  catchError,
  combineLatest,
  concatMap,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  fromEvent,
  interval,
  map,
  merge,
  Observable,
  of as observableOf,
  retry,
  Subscription,
  skipWhile,
  startWith,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { resolveURL } from 'ufo';
import wretch from 'wretch';
import { z } from 'zod';
import { versionsSchema } from '@/hooks/settings/use-versions';
import { notifyFileEndDomainChange } from '@/hooks/use-notify-file-end-domain-change';
import {
  DynamicEndpointsType,
  type EnvConfigType,
  envConfigScheme,
  type RuntimeEndpointsType,
  type RuntimeEndpointType,
  runtimeEndpointsSchema,
} from '@/models/configuration';
import { promisifyConfigurationHydration, useConfigurationStore } from '@/stores/configuration';
import { requestRestart } from '@/stores/device';
import { BASE_URL } from '@/utils/constants';
import { decrypt, getKeyIv } from '@/utils/decrypt';
import { createLogger } from '@/utils/logger';
import { timeoutPromise } from '@/utils/promise';

type EndpointMatrix = [[string[], string[]], [string[], string[]], [string[], string[]]];

export class DynamicEndpointManager {
  readonly #ensureInitialized: Promise<void>;
  #store: typeof useConfigurationStore;
  #isReady = false;
  #lastUsedDynamicEndpointIndex = 0;
  #loading = {
    env: false,
    dynamic: false,
    api: false,
    file: false,
    ws: false,
  };

  readonly #logger = createLogger('Dynamic Endpoint');
  #subscriptions: Subscription = new Subscription();

  constructor(store: typeof useConfigurationStore = useConfigurationStore) {
    this.#store = store;
    this.#ensureInitialized = this.#initialize()
      .then(() => {
        this.#logger.info('ready');
      })
      .catch((err) => {
        this.#logger.error(err);
      });
  }

  destructor() {
    this.#subscriptions.unsubscribe();
  }

  revokeApiEndpoint(revokeEndpoint: string) {
    const { setEndpoint, removeBackupEndpoints } = this.#store.getState();

    removeBackupEndpoints('API_ENDPOINTS', revokeEndpoint);

    const { endpoint, envConfig, backupEndpoints } = this.#store.getState();
    this.#raceApiEndpoints([endpoint.API_ENDPOINT, ...envConfig.defaultEndpointUrl, ...backupEndpoints.API_ENDPOINTS])
      .then(([resolvedEndpoint]) => {
        setEndpoint({
          API_ENDPOINT: resolvedEndpoint,
        });
      })
      .catch((err) => {
        this.#logger.warn(err);
      });
  }

  revokeFileEndpoint(revokeEndpoint: string) {
    const { endpoint, envConfig, backupEndpoints, setEndpoint, removeBackupEndpoints } = this.#store.getState();

    removeBackupEndpoints('FILE_ENDPOINTS', revokeEndpoint);
    this.#raceFileEndpoints([
      endpoint.FILE_ENDPOINT,
      ...envConfig.defaultFileEndpointUrl,
      ...backupEndpoints.FILE_ENDPOINTS,
    ])
      .then(([resolvedEndpoint]) => {
        setEndpoint({
          FILE_ENDPOINT: resolvedEndpoint,
        });
      })
      .catch((err) => {
        this.#logger.warn(err);
      });
  }

  revokeSocketEndpoint(revokeEndpoint: string) {
    const { endpoint, envConfig, backupEndpoints, setEndpoint, removeBackupEndpoints } = this.#store.getState();

    removeBackupEndpoints('WEBSOCKET_ENDPOINTS', revokeEndpoint);
    this.#raceSocketEndpoints([
      endpoint.WEBSOCKET_ENDPOINT,
      ...envConfig.defaultWebSocketEndpointUrl,
      ...backupEndpoints.WEBSOCKET_ENDPOINTS,
    ])
      .then(([resolvedEndpoint]) => {
        setEndpoint({
          WEBSOCKET_ENDPOINT: resolvedEndpoint,
        });
      })
      .catch((err) => {
        this.#logger.warn(err);
      });
  }

  get lastUsedDynamicEndpoint() {
    const {
      envConfig: { dynamicEndpoints },
    } = this.#store.getState();

    return dynamicEndpoints.at(this.#lastUsedDynamicEndpointIndex);
  }

  get isReady() {
    return this.#isReady;
  }

  get ensureInitialized() {
    if (this.#isReady) return Promise.resolve();

    return this.#ensureInitialized;
  }

  async #initialize() {
    await promisifyConfigurationHydration();

    const anyEmptyEndpoints$ = new Observable<[RuntimeEndpointType, RuntimeEndpointsType]>((subscriber) =>
      this.#store.subscribe(({ endpoint, backupEndpoints }) => {
        subscriber.next([endpoint, backupEndpoints]);
      }),
    ).pipe(
      startWith<[RuntimeEndpointType, RuntimeEndpointsType]>([
        this.#store.getState().endpoint,
        this.#store.getState().backupEndpoints,
      ]),
      map(
        ([endpoint, backupEndpoints]) =>
          isEmpty(endpoint.API_ENDPOINT) ||
          isEmpty(endpoint.FILE_ENDPOINT) ||
          isEmpty(endpoint.WEBSOCKET_ENDPOINT) ||
          isEmpty(backupEndpoints.API_ENDPOINTS) ||
          isEmpty(backupEndpoints.FILE_ENDPOINTS) ||
          isEmpty(backupEndpoints.WEBSOCKET_ENDPOINTS),
      ),
      distinctUntilChanged(),
    );

    const periodic$ = combineLatest([
      interval(5 * 60 * 1000).pipe(startWith(0)),
      merge(fromEvent(window, 'online'), fromEvent(window, 'offline')).pipe(
        startWith(navigator.onLine),
        map(() => navigator.onLine),
      ),
      fromEvent(document, 'visibilitychange').pipe(
        startWith(!document.hidden),
        map(() => !document.hidden),
      ),
    ]).pipe(filter(([, online, visible]) => online && visible));

    const envConfig$ = periodic$.pipe(
      skipWhile(([count]) => this.#loading.env || count % 3 !== 0),
      startWith(undefined),
      switchMap(() => this.#getEnvConfig()),
      distinctUntilChanged((prev, next) => equals(prev, next)),
      tap((envConfig) => {
        this.#store.getState().setEnvConfig(envConfig);
      }),
    );

    const dynamicEndpoints$ = combineLatest([periodic$, envConfig$, anyEmptyEndpoints$]).pipe(
      skipWhile(([, envConfig]) => !envConfig || this.#loading.dynamic),
      switchMap(() => this.#getDynamicEndpoints()),
      retry({
        resetOnSuccess: true,
        delay: (_, count) => (count >= this.#store.getState().envConfig.dynamicEndpoints.length ? EMPTY : timer(300)),
      }),
      catchError(() => EMPTY),
      filter(isNotNil),
      distinctUntilChanged((prev, next) => equals(prev, next)),
    );

    const raceEndpoints$$ = combineLatest([envConfig$, dynamicEndpoints$])
      .pipe(
        map(([envConfig, dynamicEndpoints]): EndpointMatrix => {
          const { endpoint, backupEndpoints } = this.#store.getState();

          return [
            [
              [...envConfig.defaultEndpointUrl, ...dynamicEndpoints.API_ENDPOINTS],
              [endpoint.API_ENDPOINT, ...backupEndpoints.API_ENDPOINTS],
            ],
            [
              [...envConfig.defaultFileEndpointUrl, ...dynamicEndpoints.FILE_ENDPOINTS],
              [endpoint.FILE_ENDPOINT, ...backupEndpoints.FILE_ENDPOINTS],
            ],
            [
              [...envConfig.defaultWebSocketEndpointUrl, ...dynamicEndpoints.WEBSOCKET_ENDPOINTS],
              [endpoint.WEBSOCKET_ENDPOINT, ...backupEndpoints.WEBSOCKET_ENDPOINTS],
            ],
          ];
        }),
        concatMap(([api, file, socket]) =>
          combineLatest([
            observableOf([api[0], file[0], socket[0]]),
            from(this.#raceApiEndpoints(...api)),
            from(this.#raceFileEndpoints(...file)),
            from(this.#raceSocketEndpoints(...socket)),
          ]),
        ),
      )
      .subscribe({
        next: ([endpointMatrix, api, file, socket]) => {
          const store = this.#store.getState();

          const endpoints = {
            API_ENDPOINT: api[0] || api[1],
            FILE_ENDPOINT: file[0] || file[1],
            WEBSOCKET_ENDPOINT: socket[0] || socket[1],
          };

          this.#logger.info('Endpoints', endpoints);
          store.setEndpoint(endpoints);

          const backupEndpoints = {
            API_ENDPOINTS: api[0] ? endpointMatrix[0] : undefined,
            FILE_ENDPOINTS: file[0] ? endpointMatrix[1] : undefined,
            WEBSOCKET_ENDPOINTS: socket[0] ? endpointMatrix[2] : undefined,
          };
          this.#logger.info('Backup Endpoints', backupEndpoints);
          store.setBackupEndpoints(backupEndpoints);
        },
        error: (err) => {
          this.#logger.fatal('Dynamic Endpoints', err);
          requestRestart();
        },
      });

    this.#subscriptions.add(raceEndpoints$$);
  }

  async #getEnvConfig(): Promise<EnvConfigType> {
    try {
      this.#loading.env = true;
      await new Promise<void>((resolve) => {
        const scriptTag = document.createElement('script');
        scriptTag.type = 'module';
        scriptTag.onload = () => {
          resolve();
        };
        scriptTag.src = resolveURL(BASE_URL, 'env.js', `?buster=${Date.now()}`);
        document.head.appendChild(scriptTag);
      });
      const response = window.__APP;
      return envConfigScheme.parseAsync(response);
    } catch (err) {
      this.#logger.error('Get Env', err);
      throw new DynamicEndpointError(1, 'BAD_ENV_CONFIG');
    } finally {
      this.#loading.env = false;
    }
  }

  async #getDynamicEndpoints(): Promise<RuntimeEndpointsType> {
    const {
      envConfig: { dynamicEndpoints, brand },
    } = this.#store.getState();
    let config = dynamicEndpoints.at(this.#lastUsedDynamicEndpointIndex);

    if (!config && dynamicEndpoints.at(0)) {
      this.#lastUsedDynamicEndpointIndex = 0;
      config = dynamicEndpoints.at(0);
    }

    if (!config) {
      this.#logger.fatal('Get Dynamic', 'NO_DYNAMIC_ENDPOINT_CONFIG');
      throw new DynamicEndpointError(2, 'NO_DYNAMIC_ENDPOINT_CONFIG');
    }

    const headers = new Headers();
    switch (config.type) {
      case DynamicEndpointsType.Git:
        if (config.token) headers.set('private-token', config.token);
        break;
      default:
        break;
    }

    let data = '';

    try {
      this.#loading.dynamic = true;
      data = await wretch().options({ cache: 'no-cache' }).headers(headers).get(config.source).text();

      if (config.type === DynamicEndpointsType.S3Encrypt || config.type === DynamicEndpointsType.GitEncrypt) {
        const env = this.#store.getState().envConfig.env;
        const { iv, key } = await getKeyIv(brand?.key, env);
        data = await decrypt(data, key, iv);
      }

      return runtimeEndpointsSchema.parseAsync(JSON.parse(data));
    } catch (err) {
      this.#lastUsedDynamicEndpointIndex++;
      this.#logger.fatal('Get Dynamic', 'BAD_DYNAMIC_ENDPOINT_CONFIG', err);
      return { API_ENDPOINTS: [], WEBSOCKET_ENDPOINTS: [], FILE_ENDPOINTS: [] };
    } finally {
      this.#loading.dynamic = false;
    }
  }

  async #raceApiEndpoints(
    endpoints: string[],
    storedEndpoints: string[] = [],
  ): Promise<[string | undefined, string | undefined]> {
    if (this.#loading.api) throw 'API_ENDPOINT_RACE_IN_PROGRESS';

    this.#loading.api = true;

    let targets = uniq(endpoints).filter(Boolean);
    if (isEmpty(targets)) return Promise.resolve([undefined, undefined]);

    let result = await Promise.any<string>(targets.map((endpoint) => this.#raceApiEndpoint(endpoint))).catch(() => '');

    if (result) {
      this.#loading.api = false;
      return [result, undefined];
    }

    targets = uniq(storedEndpoints).filter(Boolean);

    result = await Promise.any<string>(targets.map((endpoint) => this.#raceApiEndpoint(endpoint))).catch(() => '');

    this.#loading.api = false;
    return [undefined, result];
  }

  async #raceFileEndpoints(
    endpoints: string[],
    storedEndpoints: string[] = [],
  ): Promise<[string | undefined, string | undefined]> {
    if (this.#loading.file) throw 'FILE_ENDPOINT_RACE_IN_PROGRESS';

    this.#loading.file = true;

    let targets = uniq(endpoints).filter(Boolean);
    if (isEmpty(targets)) return Promise.resolve([undefined, undefined]);

    let result = await Promise.any<string>(targets.map((endpoint) => this.#raceFileEndpoint(endpoint))).catch(() => '');

    if (result) {
      this.#loading.file = false;
      return [result, undefined];
    }

    targets = uniq(storedEndpoints).filter(Boolean);

    result = await Promise.any<string>(targets.map((endpoint) => this.#raceFileEndpoint(endpoint))).catch(() => '');

    this.#loading.file = false;
    return [undefined, result];
  }

  async #raceSocketEndpoints(
    endpoints: string[],
    storedEndpoints: string[] = [],
  ): Promise<[string | undefined, string | undefined]> {
    if (this.#loading.ws) throw 'SOCKET_ENDPOINT_RACE_IN_PROGRESS';

    this.#loading.ws = true;

    let targets = uniq(endpoints).filter(Boolean);
    if (isEmpty(targets)) return Promise.resolve([undefined, undefined]);

    let result = await Promise.any<string>(targets.map((endpoint) => this.#raceSocketEndpoint(endpoint))).catch(
      () => '',
    );

    if (result) {
      this.#loading.ws = false;
      return [result, undefined];
    }

    targets = uniq(storedEndpoints).filter(Boolean);

    result = await Promise.any<string>(targets.map((endpoint) => this.#raceSocketEndpoint(endpoint))).catch(() => '');
    this.#loading.ws = false;
    return [undefined, result];
  }

  #raceApiEndpoint(endpoint: string): Promise<string> {
    return wretch(endpoint, { cache: 'no-cache' })
      .get('/GetVersion')
      .fetchError(() => {
        this.#store.getState().removeBackupEndpoints('API_ENDPOINTS', endpoint);
        throw 'BAD_ENDPOINT';
      })
      .json(async (res) => {
        try {
          await versionsSchema.parseAsync(res);

          return `${new URL(endpoint).origin}/Api`;
        } catch (error) {
          this.#store.getState().removeBackupEndpoints('API_ENDPOINTS', endpoint);
          throw 'BAD_ENDPOINT';
        }
      });
  }

  #raceFileEndpoint(endpoint: string): Promise<string> {
    return wretch(endpoint, { cache: 'no-cache' })
      .get(`/Health.txt?buster=${Date.now()}`)
      .fetchError(() => {
        this.#store.getState().removeBackupEndpoints('FILE_ENDPOINTS', endpoint);
        notifyFileEndDomainChange({
          domain: endpoint,
          result: 'fetch',
        }).catch((err) => {
          this.#logger.warn(err);
        });

        throw 'BAD_ENDPOINT';
      })
      .text((res) => {
        if (res !== 'Health OK!') {
          notifyFileEndDomainChange({ domain: endpoint, result: 'health' }).catch((err) => {
            this.#logger.warn(err);
          });
          this.#store.getState().removeBackupEndpoints('FILE_ENDPOINTS', endpoint);
          throw 'BAD_ENDPOINT';
        }

        notifyFileEndDomainChange({
          domain: endpoint,
          result: true,
        }).catch((err) => {
          this.#logger.warn(err);
        });
        return endpoint;
      });
  }

  #raceSocketEndpoint(endpoint: string): Promise<string> {
    return timeoutPromise(
      () =>
        new Promise<string>((resolve, reject) => {
          const ws = new WebSocket(endpoint);

          ws.onmessage = async (event) => {
            try {
              const data = await z
                .object({
                  Cmd: z.literal('Connected'),
                })
                .parseAsync(JSON.parse(event.data));

              if (data) {
                ws.close();
                resolve(endpoint);
              }
            } catch (error) {
              this.#store.getState().removeBackupEndpoints('WEBSOCKET_ENDPOINTS', endpoint);
              reject('BAD_CONNECT_HELLO');
            }
          };

          ws.onerror = (err) => {
            this.#store.getState().removeBackupEndpoints('WEBSOCKET_ENDPOINTS', endpoint);
            reject(err);
          };
        }),
      15_000,
    );
  }
}

export const dynamicEndpointManager = new DynamicEndpointManager();

/**
 * DynamicEndpointError Code
 * 1  Bad Data:  did not pass schema check
 * 2  No Data:   did not get data
 */
export class DynamicEndpointError extends Error {
  constructor(
    readonly code: number,
    message: string,
  ) {
    super(message);
  }

  static isDynamicEndpointError(error: unknown): error is DynamicEndpointError {
    return error instanceof DynamicEndpointError;
  }
}
